import { getApiRoute } from '@/lib/config/urls';

/**
 * Fire-and-forget client error reporting for the web app.
 *
 * Posts structured error payloads to the backend's `/api/client-errors`
 * endpoint so failures that happen in the browser (uncaught errors,
 * unhandled rejections, auth failures) surface in backend logs/analytics.
 *
 * Deliberately uses raw `fetch` instead of `ApiClient` — a client that is
 * failing must not depend on the client code that may be the cause.
 * All failures here are swallowed: reporting must never break the app.
 */

export interface ClientErrorPayload {
    app: 'web';
    platform: 'web';
    appVersion: string;
    url: string;
    screen?: string;
    message: string;
    errorName?: string;
    code?: string;
    stack?: string;
    email?: string;
}

const APP_VERSION =
    (import.meta.env.VITE_APP_VERSION as string | undefined) ??
    (import.meta.env.VITE_VERSION as string | undefined) ??
    'web';

// Dedupe recent reports (message + first 3 stack lines) to avoid spam loops.
const recentKeys = new Set<string>();
const RECENT_MAX = 64;

function dedupeKey(message: string, stack?: string): string {
    const stackHead = (stack ?? '').split('\n').slice(0, 3).join('\n');
    return `${message}\n${stackHead}`.trim();
}

/** Report an error to the backend. Always safe to call. */
export function reportClientError(
    input: Omit<ClientErrorPayload, 'app' | 'platform' | 'appVersion' | 'url'>,
): void {
    try {
        const key = dedupeKey(input.message, input.stack);
        if (recentKeys.has(key)) return;
        if (recentKeys.size >= RECENT_MAX) recentKeys.clear();
        recentKeys.add(key);

        const payload: ClientErrorPayload = {
            app: 'web',
            platform: 'web',
            appVersion: APP_VERSION,
            url:
                typeof window !== 'undefined' ? window.location.pathname + window.location.search : '',
            ...input,
        };

        void (async () => {
            try {
                await fetch(getApiRoute('/api/client-errors'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    // Report is best-effort: don't block page unload.
                    keepalive: true,
                });
            } catch {
                // swallow — never break the app because reporting failed
            }
        })();
    } catch {
        // swallow
    }
}

/** Register global error handlers (window.onerror + unhandledrejection). */
export function installGlobalErrorReporting(): void {
    if (typeof window === 'undefined') return;

    const seenRejectionMessages = new Set<string>();
    const REJECTION_MAX = 32;

    window.addEventListener('error', (event) => {
        const error = event.error instanceof Error ? event.error : null;
        reportClientError({
            message: error?.message || event.message || 'Uncaught error',
            errorName: error?.name || 'WindowError',
            stack: error?.stack,
            screen: 'global',
            code: 'UNCAUGHT_ERROR',
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        const message =
            reason instanceof Error
                ? reason.message
                : typeof reason === 'string'
                    ? reason
                    : 'Unhandled promise rejection';
        // Dedupe identical rejection messages (e.g. repeated fetch failures).
        if (seenRejectionMessages.has(message)) return;
        if (seenRejectionMessages.size >= REJECTION_MAX) seenRejectionMessages.clear();
        seenRejectionMessages.add(message);

        reportClientError({
            message,
            errorName:
                reason instanceof Error ? reason.name : 'UnhandledRejection',
            stack: reason instanceof Error ? reason.stack : undefined,
            screen: 'global',
            code: 'UNHANDLED_REJECTION',
        });
    });
}
